import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Sparkles, Loader2, ArrowLeft, Eye, EyeOff, Building2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function HosysAIRegister() {
  const { refreshAuth } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company_name: "", name: "", email: "", password: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.email || !form.password) {
      toast.error("Omple tots els camps obligatoris");
      return;
    }
    if (form.password.length < 8) {
      toast.error("La contrasenya ha de tenir mínim 8 caràcters");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/hosysai/register`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        // 409 = already exists
        if (res.status === 409) {
          toast.error(data.detail || "Ja està registrat", { duration: 6000 });
        } else {
          toast.error(data.detail || "Error registrant");
        }
        return;
      }
      // Store token if returned
      if (data.token) {
        try { localStorage.setItem("access_token", data.token); } catch (e) { /* noop */ }
      }
      toast.success(`Empresa "${data.venue.name}" creada correctament!`);
      if (refreshAuth) await refreshAuth();
      navigate("/HosysAI");
    } catch (err) {
      toast.error("Error de connexió amb el servidor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white flex items-center justify-center px-4 py-8 relative overflow-hidden" data-testid="hosysai-register">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-fuchsia-500/10 blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 text-sm">
          <ArrowLeft className="w-4 h-4" /> Tornar
        </Link>

        <div className="p-8 rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-bold text-lg leading-none">HOSYS<span className="text-cyan-300"> AI</span></p>
              <p className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5">Academy & Compliance</p>
            </div>
          </div>

          <h1 className="text-2xl font-bold text-white">Crea la teva empresa</h1>
          <p className="text-sm text-slate-400 mt-1">30 dies gratis · Sense targeta de crèdit</p>

          <form onSubmit={submit} className="mt-6 space-y-3">
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider flex items-center gap-1">
                <Building2 className="w-3 h-3" /> Nom de l'empresa *
              </label>
              <input
                type="text" value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-400"
                placeholder="Ex: Gestoria Puig, Restaurant XYZ..."
                required data-testid="reg-company"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider">El teu nom</label>
              <input
                type="text" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-400"
                placeholder="Nom i cognoms"
                data-testid="reg-name"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider">Email *</label>
              <input
                type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-400"
                placeholder="nom@empresa.com" required data-testid="reg-email"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider">Contrasenya * <span className="text-slate-500 normal-case">(mín. 8 caràcters)</span></label>
              <div className="mt-1 relative">
                <input
                  type={showPw ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 pr-10 text-sm text-white focus:outline-none focus:border-cyan-400"
                  required data-testid="reg-password"
                />
                <button type="button" onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-white">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-cyan-400 to-fuchsia-500 text-white font-semibold shadow-lg shadow-fuchsia-500/30 hover:shadow-fuchsia-500/50 transition-all disabled:opacity-50 flex items-center justify-center gap-2 mt-4"
              data-testid="reg-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Crear empresa
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/5 text-center">
            <p className="text-sm text-slate-400">
              Ja tens compte?{" "}
              <Link to="/login" className="text-cyan-300 hover:text-cyan-200 font-medium">Accedir</Link>
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          Powered by <a href="https://hosys.es" className="text-cyan-400 hover:text-cyan-300">Ho-Sys</a>
        </p>
      </div>
    </div>
  );
}
